// src/pages/CriarCampanha.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function CriarCampanha() {
    const [assistentes, setAssistentes] = useState([]);
    const [numeros, setNumeros] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        nome: '',
        objetivo: '',
        tipo: 'vendas',
        contatos: '',
        assistantId: '',
        phoneNumberId: '',
        agendamento: '',
        modo: 'ativa',
    });

    const fetchInitialData = useCallback(async () => {
        const loadingToast = toast.loading('Carregando dados necessários...');
        try {
            const resAssistentes = await axios.get(`http://localhost:3000/api/assistentes/master`);
            const resNumeros = await axios.get(`http://localhost:3000/api/phoneNumbers`);
            
            setAssistentes(resAssistentes.data);
            setNumeros(resNumeros.data);
            toast.dismiss(loadingToast);
        } catch (err) {
            toast.error('Não foi possível carregar assistentes e números.', { id: loadingToast });
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const loadingToast = toast.loading('Criando campanha...');

        if (!formData.nome || !formData.objetivo || !formData.assistantId || !formData.phoneNumberId || !formData.contatos) {
            toast.error('Preencha todos os campos obrigatórios.', { id: loadingToast });
            setIsSubmitting(false);
            return;
        }

        const novaCampanha = {
            ...formData,
            listaDeContatos: formData.contatos.split('\n').map(c => c.trim()).filter(c => c),
        };
        delete novaCampanha.contatos;

        try {
            await axios.post(`http://localhost:3000/api/campanhas`, novaCampanha);
            toast.success('Campanha criada com sucesso!', { id: loadingToast });
            navigate('/campanhas');
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao criar campanha.';
            toast.error(errorMessage, { id: loadingToast });
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Estilos corrigidos
    const inputStyle = "w-full px-3 py-2 mt-1 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-500";
    const labelStyle = "text-sm font-medium text-gray-300";

  return (
    <div className="space-y-6">
            <div className="max-w-4xl mx-auto">
                {/* Cabeçalho */}
                <div className="flex items-center mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 mr-4 text-gray-400 hover:text-white rounded-full hover:bg-gray-700">
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <h1 className="text-3xl font-bold">Criar Nova Campanha</h1>
                </div>
                
                <form onSubmit={handleSubmit} className="bg-gray-800 bg-opacity-60 p-8 rounded-xl shadow-lg space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="nome" className={labelStyle}>Nome da Campanha</label>
                            <input id="nome" name="nome" type="text" value={formData.nome} onChange={handleChange} className={inputStyle} required placeholder="Ex: Campanha de Vendas - Julho" />
                        </div>
                        <div>
                            <label htmlFor="objetivo" className={labelStyle}>Objetivo da Campanha</label>
                            <input id="objetivo" name="objetivo" type="text" value={formData.objetivo} onChange={handleChange} className={inputStyle} required placeholder="Ex: qualificar lead, cobrança amigável" />
                        </div>
                        <div>
                            <label htmlFor="tipo" className={labelStyle}>Tipo de Campanha</label>
                            <select id="tipo" name="tipo" value={formData.tipo} onChange={handleChange} className={inputStyle}>
                                <option value="vendas">Vendas</option>
                                <option value="suporte">Suporte</option>
                                <option value="pesquisa">Pesquisa</option>
                                <option value="cobranca">Cobrança</option>
                            </select>
                        </div>
                    </div>
                    
                    <div>
                        <label htmlFor="contatos" className={labelStyle}>Lista de Contatos</label>
                        <textarea
                            id="contatos"
                            name="contatos"
                            value={formData.contatos}
                            onChange={handleChange}
                            className={`${inputStyle} h-40 font-mono`}
                            placeholder="Cole a lista de números, um por linha. Ex: +5511999998888"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="assistantId" className={labelStyle}>Assistente de Voz</label>
                            <select id="assistantId" name="assistantId" value={formData.assistantId} onChange={handleChange} className={inputStyle} required>
                                <option value="">Selecione um assistente</option>
                                {assistentes.map(a => <option key={a.vapiId} value={a.vapiId}>{a.nome || a.name}</option>)}
                            </select>
                        </div>
                         <div>
                            <label htmlFor="phoneNumberId" className={labelStyle}>Número de Origem (Bina)</label>
                            <select id="phoneNumberId" name="phoneNumberId" value={formData.phoneNumberId} onChange={handleChange} className={inputStyle} required>
                                <option value="">Selecione um número</option>
                                {numeros.map(n => <option key={n._id} value={n._id}>{n.name} ({n.number})</option>)}
                            </select>
                        </div>
                    </div>
                    
                     <div>
                        <label htmlFor="agendamento" className={labelStyle}>Agendar Envio (Opcional)</label>
                        <input id="agendamento" name="agendamento" type="datetime-local" value={formData.agendamento} onChange={handleChange} className={inputStyle} />
                        <p className="text-xs text-gray-500 mt-1">Deixe em branco para enviar imediatamente após a criação.</p>
                    </div>

                    {/* Botão de Submissão */}
                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={isSubmitting} className="flex items-center justify-center w-full md:w-auto px-6 py-3 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50">
                            <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                            {isSubmitting ? 'Criando Campanha...' : 'Criar e Agendar Campanha'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}